import React, { useEffect, useRef, useState } from 'react';
import styles from './containerList.module.css';
import SubmitButton from '@/ctComponents/submitButton';
import IconButton from '@/ctComponents/IconButton';
import Table from '@/ctComponents/Table';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import {
  AutoBookContainersOption,
  AutoBookCreateContainers,
  SaveApiResMessage,
} from '@/actions';
import LicenseInput from './LicenseInput';
import { useNotify } from 'react-admin';
import MultiSelectBoxAutobook from '@/ctComponents/MultiSelectBoxAutobook';

const AddNewContainersModal = ({
  ContainerOptions,
  setShow,
  setContainers,
  transformAppointmentsData,
}) => {
  const wrapperRef = useRef(null);
  const cardRef = useRef(null);
  const ctRef = useRef(null);
  const notify = useNotify();
  const [loading, setLoading] = useState('');
  const [addContainers, setAddContainers] = useState();
  const [container, setContainer] = useState([]);
  const [hint, setHint] = useState('');
  const [open, setOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [fixed, setFixed] = useState(false);
  const [scrollLeft, setScrollLeft] = useState('-24px');
  const [tableWrapperWidth, setTableWrapperWidth] = useState(0);
  const [selectedOptionsCount, setSelectedOptionsCount] = useState(0);
  const [canAddToList, setCanAddToList] = useState(false);
  const multiSelectRef = useRef(null);

  useEffect(() => {
    const body = ctRef.current;
    if (!body) return;

    const handleResize = () => {
      setTableWrapperWidth(
        window.innerWidth < 897
          ? 655
          : body.clientWidth - (body.clientHeight > 250 ? 17 : 9)
      );
    };
    handleScroll(body);
    // Set width immediately on mount
    handleResize();
    body.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      body.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [ctRef, tableData]);

  const handleSubmit = () => {
    const containers = tableData.map(
      ({ ssl, specification, idx, ...rest }) => rest
    );
    AutoBookCreateContainers({ containers: containers })
      .then((res) => {
        SaveApiResMessage({
          title: 'Containers Added',
          message: 'Container(s) added to AutoBook list successfully',
          mode: 'success',
        });
        // notify(res.message, 'success');
        setContainers(transformAppointmentsData(res?.data?.autobooks?.data));
      })
      .catch((err) => {
        // SaveApiResMessage({
        //   title: 'Containers Not Added',
        //   message: 'Containers not added successfully',
        //   mode: 'error',
        // });
      });
    setShow(false);
    // setContainers(containers);
  };
  const handleScroll = (body) => {
    if (body.target?.scrollTop > 100) {
      setFixed(true);
    } else {
      setFixed(false);
    }
  };
  useEffect(() => {
    AutoBookContainersOption().then((res) => {
      setAddContainers({
        containers: res.data?.containers?.map((container) => ({
          value: container.id,
          label: container.name,
          specification_id: container?.cabin?.specification_id,
          cabin_id: container?.cabin_id,
          ssl_id: container?.cabin?.company_id,
          is_verified:
            container?.cabin?.specification_id &&
            container?.cabin?.company_id &&
            true,
        })),
        ssls: res.data?.containers?.map((ssl) => ({
          value: ssl?.cabin?.specification_id,
          label: ssl?.cabin?.specification_id,
        })),
        license_plates: res.data?.containers?.map((license_plate) => ({
          value: license_plate,
          label: license_plate,
        })),
        sizes: res.data?.containers?.map((company_id) => ({
          value: company_id?.cabin?.company_id,
          label: company_id?.cabin?.company_id,
        })),
      });
    });
  }, []);
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleScrollX = () => {
      setScrollLeft(card.scrollLeft - 24);
    };

    card.addEventListener('scroll', handleScrollX);

    return () => {
      card.removeEventListener('scroll', handleScrollX);
    };
  }, []);

  useEffect(() => {
    const body = wrapperRef.current;
    if (!body) return;
    handleScroll(body);
    body.addEventListener('scroll', handleScroll);
    return () => {
      body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const updateButtonState = () => {
      const selectedOptions =
        multiSelectRef.current?.getSelectedOptions?.() || [];
      const searchTerm = multiSelectRef.current?.getSearchTerm?.() || '';

      const hasSelectedOptions = selectedOptions.length > 0;
      const hasInputText = searchTerm.trim().length > 0;

      setSelectedOptionsCount(selectedOptions.length);
      setCanAddToList(hasSelectedOptions || hasInputText);
    };

    // Update state initially and set up interval to check for changes
    updateButtonState();
    const interval = setInterval(updateButtonState, 100);

    return () => clearInterval(interval);
  }, []);

  // Sync component's final list with tableData when items are deleted
  useEffect(() => {
    if (multiSelectRef.current) {
      // Get container names from tableData
      const tableContainerNames = tableData.map((item) => item.name);

      // Update component's final list to match tableData
      const currentFinalList = multiSelectRef.current.getFinalList?.() || [];
      const updatedFinalList = currentFinalList.filter((item) =>
        tableContainerNames.includes(item.label)
      );

      // Update component's final list
      if (multiSelectRef.current?.updateFinalList) {
        multiSelectRef.current.updateFinalList(updatedFinalList);
      }
    }
  }, [tableData]);

  const columns = [
    { field: 'idx', label: '#', align: 'center', width: '30px' },
    {
      field: 'name',
      label: 'Container#',
      align: 'center',
      width: '110px',
    },
    {
      field: 'size',
      label: 'Size',
      align: 'center',
      width: '150px',
      required: 'true',
      component: (row) => {
        const rowDataSize =
          ContainerOptions[0]?.options.filter(
            (option) => option.value === row.specification_id
          ) || {};
        const rowData = tableData.find((item) => item.name === row.name) || {};
        return row.is_verified ? (
          <span>{rowDataSize[0]?.label}</span>
        ) : (
          <CustomSelectEx
            placeholder="Enter size"
            value={rowData.specification}
            onChange={(item) =>
              setTableData((prev) =>
                prev.map((r) =>
                  r.name === row.name
                    ? {
                        ...r,
                        specification: item,
                        specification_id: item.value,
                      }
                    : r
                )
              )
            }
            options={ContainerOptions[0]?.options}
            dropdown={true}
            height={'25px'}
            mini={true}
          />
        );
      },
    },
    {
      field: 'ssl',
      label: 'SSL',
      align: 'center',
      width: '150px',
      required: 'true',
      component: (row) => {
        const rowDataSSL =
          ContainerOptions[1]?.options.filter(
            (option) => option.value === row.ssl_id
          ) || {};
        const rowData = tableData.find((item) => item.name === row.name) || {};
        return row.is_verified ? (
          <span>{rowDataSSL[0]?.label}</span>
        ) : (
          <CustomSelectEx
            placeholder="Enter SSL"
            value={rowData.ssl || ''}
            onChange={(item) =>
              setTableData((prev) =>
                prev.map((r) =>
                  r.name === row.name
                    ? {
                        ...r,
                        ssl: item,
                        ssl_id: item.value,
                      }
                    : r
                )
              )
            }
            options={ContainerOptions[1]?.options}
            dropdown={true}
            height={'25px'}
            fontSize={'11px !important'}
            mini={true}
          />
        );
      },
    },
    {
      field: 'license',
      label: 'License',
      align: 'center',
      width: '110px',
      component: (row) => {
        const rowData = tableData.find((item) => item.name === row.name) || {};
        return (
          <LicenseInput
            initialValue={rowData.license_plate}
            onBlurSave={(val) =>
              setTableData((prev) =>
                prev.map((r) =>
                  r.name === row.name ? { ...r, license_plate: val } : r
                )
              )
            }
          />
        );
      },
    },
  ];
  const handleAddTolist = () => {
    // Get selected options from the component
    const selectedOptions =
      multiSelectRef.current?.getSelectedOptions?.() || [];

    // Get manually typed values from the component
    const searchTerm = multiSelectRef.current?.getSearchTerm?.() || '';

    if (selectedOptions.length === 0 && !searchTerm.trim()) return;

    const seenNames = new Set();
    const uniqueContainers = [];
    const uniqueContainerOptions = [];

    // Add existing tableData names to seenNames to avoid duplicates
    tableData.forEach((item) => seenNames.add(item.name));

    // Process selected options
    for (const item of selectedOptions) {
      if (!seenNames.has(item.label)) {
        const containerSelected = addContainers?.containers.find(
          (cn) => cn.value === item.value
        );
        seenNames.add(item.label);
        uniqueContainers.push({
          id: item?.label !== item?.value ? item.value : null,
          name: item.label,
          license_plate: item.license_plate ?? null,
          ssl:
            ContainerOptions[0].options.find(
              (option) => option.value === containerSelected?.ssl_id
            ) ?? null,
          specification:
            ContainerOptions[0].options.find(
              (option) => option.value === containerSelected?.specification_id
            ) ?? null,
          specification_id: containerSelected?.specification_id ?? null,
          ssl_id: containerSelected?.ssl_id ?? null,
          is_verified: containerSelected?.is_verified ?? false,
        });

        uniqueContainerOptions.push(item);
      }
    }

    // Process manually typed values - only add comma-separated values with exactly 11 characters
    if (searchTerm.trim()) {
      const commaSeparatedValues = searchTerm
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length === 11);

      commaSeparatedValues.forEach((value) => {
        if (!seenNames.has(value)) {
          seenNames.add(value);
          uniqueContainers.push({
            id: null,
            name: value,
            license_plate: null,
            ssl: null,
            specification: null,
            specification_id: null,
            ssl_id: null,
            is_verified: false,
          });
        }
      });
    }

    const containersWithIds = uniqueContainers.map((item, index) => ({
      ...item,
      idx: tableData.length + index + 1, // Continue numbering from existing items
    }));

    // Append to existing tableData instead of replacing
    setTableData((prev) => [...prev, ...containersWithIds]);
    setContainer(uniqueContainerOptions);

    // Add selected options to final list in the component
    if (multiSelectRef.current?.addSelectedToFinalList) {
      multiSelectRef.current.addSelectedToFinalList();
    }
  };

  const icons = [
    {
      name: 'trashIcon',
      color: '#EAEAEA',
      handleIcon: (row) => {
        console.log('row', row);

        const containerName = row.name;
        setContainer((prev) =>
          prev.filter(
            (item) =>
              item.label !== containerName && item.value !== containerName
          )
        );

        setTableData((prev) => {
          console.log('prev', prev);
          return prev.filter((item) => item.name !== containerName);
        });
      },
    },
  ];
  return (
    <div className={styles.ModalContainer} ref={cardRef}>
      {hint && <span className={styles.hint}>{hint}</span>}
      <span className={styles.subTitle}>
        Enter container numbers to add them to your Autobook list.
      </span>
      <div className="space-y-4 lg:space-y-0 flex flex-col lg:flex-row justify-between lg:items-end">
        <div className="w-full lg:w-4/5">
          <MultiSelectBoxAutobook
            title="Container"
            placeholder="Enter containers separated by commas"
            value={container}
            multi={true}
            onChange={(item) => setContainer(item)}
            options={addContainers?.containers}
            AddOption={true}
            Err={'Incorrect Container number'}
            onCreateOption={(inputValue) => {
              // This won't add to the list, just logs if needed
            }}
            required={true}
            ref={multiSelectRef}
          />
        </div>

        <IconButton
          disable={!canAddToList}
          onClick={handleAddTolist}
          icon={'Add'}
          text={'Add to List'}
          color={'#131617'}
          size={'bigHeight'}
        />
      </div>

      {/* <div
        className={styles.addItemButtonContainer}
        style={{ marginTop: open ? '100px' : '24px' }}>
        <IconButton
          disable={container?.length === 0}
          onClick={handleAddTolist}
          icon={'Add'}
          text={'Add to List'}
          color={'#131617'}
          size={'big'}
        />
      </div> */}
      {tableData?.length > 0 && (
        <div className={styles.tableContainer} ref={ctRef}>
          <Table
            parent={'addContainerAutobook'}
            wrapperRef={wrapperRef}
            stickyHeader={true}
            maxHeight={'250px'}
            columns={columns}
            data={tableData || []}
            totalCount={0}
            keyField="id"
            componentName={'AddNewContainersModal'}
            getRowStyle={() => ({
              width: 'webkit-fill-available',
              height: '34px',
              fontSize: '11px',
              borderTop: 'none',
              borderRadius: '0px',
              paddingBottom: '2px',
              overflow: 'hidden !important',
            })}
            noGap={true}
            icons={icons}
            fixed={fixed}
            scrollLeft={scrollLeft}
            topScroll={ctRef?.current?.scrollHeight}
            tableWrapperWidth={tableWrapperWidth}
            mini={true}
            scroll={true}
            max-height={'220px'}
            minWidth={'661px'}
          />
        </div>
      )}
      <div className={styles.buttonContainer}>
        <SubmitButton
          text={'Submit'}
          onClick={handleSubmit}
          loading={loading}
          disable={
            !tableData?.length ||
            !tableData?.every((item) => item.specification_id && item.ssl_id)
          }
        />
      </div>
    </div>
  );
};

export default AddNewContainersModal;
